import {
  Client,
  ClientSummary,
  ErrorResponse,
  FinalizeSummariesRequest,
  RevertFinalizedSummaryRequest,
} from '../api/dtos/dtos';
import * as React from 'react';
import accountProvider from '../account/AccountProvider';
import {RouteComponentProps, withRouter} from 'react-router';
import * as paths from '../paths';
import {IdPageUrlParams} from '../paths';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {DatePicker} from 'antd';
import * as moment from 'moment';
import * as qs from 'query-string';
import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import furoApi from '../api/FuroApi';
import {MOMENT_DATE_FORMAT, MOMENT_YEAR_MONTH_FORMAT} from '../utils/date-utils';
import * as formUtils from '../utils/form-utils';
import FormItem from 'antd/lib/form/FormItem';
import ClientDetailsAlert from '../client-details/ClientDetailsAlert';
import ClientRentalSummaryTable from './ClientRentalSummaryTable';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import {showSaveErrorNotification} from "../utils/dialogs";
import ClientRentalSummaryOverrideModal from "./ClientRentalSummaryOverrideModal";
import ClientRentalSummaryRemindersModal from "./ClientRentalSummaryRemindersModal";
import {
  ClientRentalSummary,
  ClientRentalSummaryGridRequest,
  ClientRentalSummaryGridResponse,
  InvoiceEntry
} from "../api/dtos/summaries";
import {InvoiceSource} from "../invoicing/invoice";
import {downloadFile} from '../utils/file-utils';

const {MonthPicker} = DatePicker;

interface State {
  client?: ClientSummary;
  error?: ErrorResponse;
  data?: ClientRentalSummaryGridResponse;
  selectedYear: number;
  selectedMonth: number;
  editedSummary?: ClientRentalSummary;
  editedSummaryForReminders?: ClientRentalSummary;
}

const monthPickerDisabledDateFn = (current: moment.Moment | null): boolean => {
  return current !== null && current.isAfter(moment(), 'month');
};

class ClientRentalSummaryPage extends React.Component<RouteComponentProps<IdPageUrlParams>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<IdPageUrlParams>) {
    super(props);

    const urlParams = qs.parse(props.location.search);
    const {year, month} = urlParams;

    this.state = {
      selectedYear: year ? Number(year) : moment().get('year'),
      selectedMonth: month ? Number(month) : moment().get('month'),
    };
  }

  public componentDidMount() {
    this.reload();
    this.loadClient();
  }

  public render() {
    const {
      client,
      selectedYear,
      selectedMonth,
      error,
      editedSummary,
      editedSummaryForReminders,
      data
    } = this.state;
    const monthPickerValue = moment().year(selectedYear).month(selectedMonth);

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ClientRentalSummaryOverrideModal
          summary={editedSummary}
          onSubmit={this.onSummaryFormSubmit}
          onCancel={this.onSummaryFormCancel}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
        />

        <ClientRentalSummaryRemindersModal
          summary={editedSummaryForReminders}
          onSubmit={this.onRemindersEditorSubmit}
          onCancel={this.onRemindersEditorCancel}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
        />

        <FormItem {...formUtils.formItemLayout} label={locales.month_capitalized}>
          <MonthPicker
            format={MOMENT_YEAR_MONTH_FORMAT}
            value={monthPickerValue}
            disabledDate={monthPickerDisabledDateFn}
            onChange={this.onMonthPickerChange}
            placeholder={locales.choose}
            allowClear={false}
          />
        </FormItem>

        {client && (
          <FormItem {...formUtils.formItemLayout} label={locales.client}>
            <ClientDetailsAlert closable={false} client={client}/>
          </FormItem>
        )}

        <ClientRentalSummaryTable
          data={data}
          onViewRentalClick={this.onViewRentalClick}
          onViewReturnalClick={this.onViewReturnalClick}
          onEditSummaryClick={this.onEditSummaryClick}
          onViewCommentsClick={this.onViewCommentsClick}
          onFinalizeSummaries={this.onFinalizeSummaries}
          onRevertFinalizedSummary={this.onRevertFinalizedSummary}
          onCreateInvoice={this.onCreateInvoice}
          onExportClick={this.onExportClick}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private onMonthPickerChange = (date: moment.Moment) => {
    const year = date.get('year');
    const month = date.get('month');
    const clientId = this.getClientIdFromUrl();
    this.props.history.replace(paths.clientSummaryPath(clientId, year, month));
    this.setState({selectedYear: year, selectedMonth: month,}, () => this.reload());
  };

  private getClientIdFromUrl = (): number => {
    return Number(this.props.match.params.id);
  };

  private loadClient = () => {
    const clientId = this.getClientIdFromUrl();
    const promise = furoApi.clients.get(clientId)
      .then(this.onLoadClientSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadClientSuccess = (response: Client) => {
    this.setState({client: response,});
    setAppHeaderSubtitle(this.props, response.name);
  };

  private onExportClick = () => {
    const clientId = this.getClientIdFromUrl();
    const {client, selectedYear, selectedMonth} = this.state;
    const request: ClientRentalSummaryGridRequest = {
      clientId,
      year: selectedYear,
      month: selectedMonth,
    };
    const yearMonth = moment([selectedYear, selectedMonth]).format('YYYY-MM');
    const clientName = (client ? client.name : String(clientId)).replace(/\s+/g, '_');
    const promise = furoApi.users.getSelectedCompanyInfo()
      .then(company => {
        const prefix = company.namingPrefix ? `${company.namingPrefix}-` : '';
        const fileName = `${prefix}Összesítő-${clientName}-${yearMonth}.xls`;
        return furoApi.summaries.exportClientRentalSummaries(request)
          .then(buffer => {
            downloadFile(new Blob([buffer], {type: 'application/vnd.ms-excel'}), fileName);
            this.setState({error: undefined});
          });
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private reload = () => {
    const clientId = this.getClientIdFromUrl();
    const {selectedYear, selectedMonth} = this.state;
    const request: ClientRentalSummaryGridRequest = {
      clientId,
      year: selectedYear,
      month: selectedMonth,
    };
    const promise = furoApi.summaries.getClientRentalSummaries(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: ClientRentalSummaryGridResponse) => {
    this.setState({
      data: response,
      error: undefined,
    });
  };

  private onApiError = (error: ErrorResponse) => {
    showSaveErrorNotification();
    this.setState({error})
  };

  private onSummaryFormCancel = () => {
    this.setState({editedSummary: undefined,});
  };

  private onSummaryFormSubmit = () => {
    this.onSummaryFormCancel();
    this.reload();
  };

  private onViewRentalClick = (rentalId: number) => {
    this.props.history.push(paths.viewRentalPath(rentalId));
  };

  private onViewReturnalClick = (returnalId: number) => {
    this.props.history.push(paths.viewReturnalPath(returnalId));
  };

  private onEditSummaryClick = (record: ClientRentalSummary) => {
    this.setState({editedSummary: record,});
  };

  private onViewCommentsClick = (record: ClientRentalSummary) => {
    this.setState({editedSummaryForReminders: record,});
  };

  private onRemindersEditorCancel = () => {
    this.setState({editedSummaryForReminders: undefined,});
  };

  private onRemindersEditorSubmit = () => {
    this.onRemindersEditorCancel();
    this.reload();
  };

  private onFinalizeSummaries = (items: ClientRentalSummary[]) => {
    const {selectedYear, selectedMonth} = this.state;
    const rentalItemIds = items.map(i => i.rentalItem.id!);

    const request: FinalizeSummariesRequest = {
      clientId: this.getClientIdFromUrl(),
      sourceIds: rentalItemIds,
      year: selectedYear,
      month: selectedMonth,
    };

    const promise = furoApi.summaries.finalizeSummaries(request)
      .then(this.reload)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onRevertFinalizedSummary = (item: ClientRentalSummary) => {
    const {selectedYear, selectedMonth} = this.state;

    const request: RevertFinalizedSummaryRequest = {
      clientId: this.getClientIdFromUrl(),
      sourceId: item.rentalItem.id!,
      year: selectedYear,
      month: selectedMonth,
    };

    const promise = furoApi.summaries.revertFinalizedSummary(request)
      .then(this.reload)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onCreateInvoice = (items: ClientRentalSummary[]) => {
    if (!accountProvider.isAdmin()) {
      return;
    }
    const entries: InvoiceEntry[] = items.map(i => {
      const price =
        i.override
          ? (i.override.total == null ? i.override.price : (i.override.total / i.override.amount))
          : (i.total == null || i.amount == null ? i.rentalItem.price : (i.total / i.amount));
      return {
        sourceEntityId: i.rentalItem.id!,
        name: i.rentalItem.item.name!!,
        price: Number(price.toFixed(1)),
        amount: i.override?.amount ?? i.amount,
        amountType: i.override?.amountType ?? i.rentalItem.amountType,
        total: i.override?.total ?? i.total,
        comment: i.rentalItem.comment,
      }
    })

    const today = moment();
    const selectedMonth = moment([this.state.selectedYear, this.state.selectedMonth]);
    const monthStart = selectedMonth.format(MOMENT_DATE_FORMAT);
    const monthEnd = (selectedMonth.isSame(today, 'month') ? today : selectedMonth.endOf('month')).format(MOMENT_DATE_FORMAT);

    this.props.history.push(paths.createInvoicePath, {
      client: this.state.client,
      entries,
      year: this.state.selectedYear,
      month: this.state.selectedMonth,
      source: InvoiceSource.RENTAL,
      invoiceComment: locales.invoiceInitialCommentTemplate(monthStart, monthEnd),
    });
  };

}

export default withRouter(ClientRentalSummaryPage);



// WEBPACK FOOTER //
// ./src/summary/ClientRentalSummaryPage.tsx